import _ from "lodash";
import moment from "moment-timezone";
import React from "react";
import Select from "react-select";

import { BehavioralGoalsRating } from "@/features/notes/(v2)/components/BehavioralGoalsRating";
import { BehavioralMentalStatus } from "@/components/Provider/PatientView/MemberProfile/MemberProfileNotesMentalStatus";
import { BehavioralSymptomTrend } from "@/features/notes/(v2)/(guided-session)/components/MemberProfileNotesSymptomTrend";
import CardSecondary from "@/components/UI/Card/CardSecondary";
import { ExpandingSelect } from "@/components/UI/ExpandingSelect";
import { ExpandingTextArea } from "@/components/UI/ExpandingTextArea";
import { GenericMultiselect } from "@/features/notes/(v2)/(guided-session)/components/MemberProfileNotesGenericMultiselect";
import { IndividualSymptomTrend } from "@/features/notes/(v2)/(guided-session)/components/IndividualSymptomTrend";
import { joinDiagnosis } from "@/features/notes/(v2)/helpers/workflow-functions";
import { PrescribeLabFindings } from "@/features/notes/(v2)/(guided-session)/components/MemberProfileNotesLabFindings";
import { RATING_OPTION_LABEL_MAP } from "@/constants/rating";

export const PrescribeSymptomTrends = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { progressNote } = state;

  return (
    <>
      <ExpandingTextArea
        label="How are you?"
        onChange={(e) => {
          noteEditHandler("symptom_trend_notes", e.target.value);
        }}
        value={progressNote.content.symptom_trend_notes}
      />
      <IndividualSymptomTrend answer={progressNote.content.symptom_trends.overall} />
      {progressNote.content.last_behavioral_note && (
        <div className="m-3">
          <BehavioralSymptomTrend
            editable={false}
            editHandler={noteEditHandler}
            note={progressNote.content.last_behavioral_note}
            patientAnswersEditable={false}
            showProvider={true}
          />
        </div>
      )}
    </>
  );
};

const PRESCRIPTION_MONITORING_LINKS = {
  "I-STOP": "https://commerce.health.state.ny.us/doh2/applinks/cspnp/singlePatientSearch",
};

export const RATING_OPTIONS_NONE_MUCH_LABEL_MAP = {
  0: "None",
  1: "Little",
  2: "Some",
  3: "Much",
  4: "Very Much",
};

const MEDICATION_IMPACT_SECTIONS = [
  {
    key: "benefits",
    labels: RATING_OPTIONS_NONE_MUCH_LABEL_MAP,
    title: "Medication Benefits",
  },
];

export const PrescribeMedicationImpact = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { progressNote } = state;

  let content = progressNote.content;
  let prescriptionMonitoring = content.prescription_monitoring;
  let medicationTrends = content.medication_trends;
  let medications = content.medications.filter((medication) => medication.med_changes !== "ADD");

  const clickIStop = () => {
    noteEditHandler("prescription_monitoring", {
      clicked: true,
      last_viewed: moment.utc().format(),
      type: prescriptionMonitoring.type,
    });
  };

  const handleMedicationChange = (e, index, key, labels) => {
    let editCopyClone = _.cloneDeep(medications);

    editCopyClone[index][key] = parseInt(e.target.value);
    editCopyClone[index][key + "_labels"] = labels;
    noteEditHandler("medications", editCopyClone);
  };

  let prescriptionMonitoringLastViewed;

  if (!prescriptionMonitoring.last_viewed) {
    prescriptionMonitoringLastViewed = "Not viewed yet ";
  } else {
    prescriptionMonitoringLastViewed = moment
      .utc(prescriptionMonitoring.last_viewed)
      .local()
      .format("MM/DD/YYYY");
  }

  return (
    <>
      <ExpandingTextArea
        label="Notes"
        onChange={(e) => {
          noteEditHandler("medication_impact_notes", e.target.value);
        }}
        value={content.medication_impact_notes}
      />

      <CardSecondary className="px-3 py-2 m-3">
        <div className="row">
          <div className="col-6">
            <p className="float-left tw:font-bold">{prescriptionMonitoring.type}</p>
          </div>
          <div className="col-6">
            <a
              className="float-right"
              href={PRESCRIPTION_MONITORING_LINKS[prescriptionMonitoring.type]}
              onClick={clickIStop}
              rel="noreferrer"
              style={{
                borderBottom: "1px",
                color: "#0000EE",
              }}
              target="_blank"
            >
              Open Link
            </a>
          </div>
        </div>
        <p>Last Viewed: {prescriptionMonitoringLastViewed}</p>
      </CardSecondary>

      {["frequency", "effectiveness", "problems", "stop"]
        .filter((key) => medicationTrends[key])
        .map((key) => (
          <IndividualSymptomTrend answer={medicationTrends[key]} key={key} />
        ))}

      {medications.map((medication, index) => {
        return (
          <CardSecondary className="p-3 m-3" key={medication.medication_name || index}>
            <p className="my-2 tw:font-bold">Medication: {medication.medication_name}</p>
            {MEDICATION_IMPACT_SECTIONS.map((section) => {
              return (
                <>
                  <p className="my-2 tw:font-bold">{section.title}</p>
                  <div className="col-12 d-flex justify-content-start">
                    {Object.values(section.labels).map((optionLabel) => (
                      <div className="w-25 fs-14 text-center tw:font-bold" key={optionLabel}>
                        <div className="m-auto" style={{ width: 50 }}>
                          {optionLabel}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="col-12 d-flex justify-content-start">
                    <BehavioralGoalsRating
                      isEditing={true}
                      onClick={(e) => handleMedicationChange(e, index, section.key, section.labels)}
                      optionLabelMap={section.labels}
                      progress={medication}
                      ratingKey={section.key}
                    />
                  </div>
                </>
              );
            })}
          </CardSecondary>
        );
      })}

      <GenericMultiselect
        className="m-3"
        editable={true}
        editHandler={noteEditHandler}
        isCheckboxes={true}
        labelClass="clr-sec"
        note={progressNote}
        profileView={false}
        sectionKey="side_effects"
        sectionLabel="Side Effect(s)"
      />
    </>
  );
};

export const PrescribeMedicalInformation = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { progressNote } = state;

  let content = progressNote.content;
  let lastBehavioralNote = progressNote.content.last_behavioral_note;

  let behavioralContent = <></>;

  if (lastBehavioralNote) {
    let providerInfo = lastBehavioralNote.signature + " " + lastBehavioralNote.session_date;

    behavioralContent = (
      <>
        <ExpandingSelect
          className="m-3 grey-container"
          disabled
          label={`Changes/Updates  ${providerInfo}`}
          labelClass="tw:font-bold clr-sec"
          options={lastBehavioralNote.content.changes}
        />

        <ExpandingSelect
          className="m-3 grey-container"
          disabled
          label={`Health Care Use  ${providerInfo}`}
          labelClass="tw:font-bold clr-sec"
          options={lastBehavioralNote.content.health_care_use}
        />
      </>
    );
  }

  return (
    <>
      <ExpandingTextArea
        label="Notes"
        onChange={(e) => {
          noteEditHandler("medical_information_notes", e.target.value);
        }}
        value={content.medical_information_notes}
      />

      {behavioralContent}

      <ExpandingSelect
        className="m-3 grey-container"
        disabled
        label="Medical Problems"
        labelClass="tw:font-bold clr-sec"
        options={progressNote.content.medical_problems}
      />

      <PrescribeLabFindings editable={true} editHandler={noteEditHandler} note={progressNote} />
      <BehavioralMentalStatus
        editable={true}
        editHandler={noteEditHandler}
        note={progressNote}
        showTitle={true}
      />
    </>
  );
};

export const MED_CHANGES_OPTIONS = [
  { label: "Increase", value: "INCREASE" },
  { label: "Decrease", value: "DECREASE" },
  { label: "Maintain", value: "MAINTAIN" },
  { label: "Discontinue", value: "DISCONTINUE" },
];

export const PrescribeAssessmentPlan = (componentProps) => {
  let { noteEditHandler, state } = componentProps;
  let { patientDiagnosis, progressNote } = state;

  let content = progressNote.content;
  let diagnosisProgress = content.diagnosis_progress;
  let medications = content.medications;
  let allDiagnosis = joinDiagnosis(patientDiagnosis);

  const handleDiagnosisProgressChange = (e, index, key, isNumber = false) => {
    let editCopyClone = _.cloneDeep(diagnosisProgress);

    editCopyClone[index][key] = isNumber ? parseInt(e.target.value) : e.target.value;
    noteEditHandler("diagnosis_progress", editCopyClone);
  };

  const addMedication = () => {
    let editCopyClone = _.cloneDeep(medications);

    editCopyClone.push({
      adherence: null,
      amount: "",
      benefits: null,
      date: moment.utc().format(),
      diagnosis: [],
      dosage: "",
      med_changes: "ADD",
      medication_name: "",
      side_effect: null,
    });
    noteEditHandler("medications", editCopyClone);
  };

  const deleteMedication = (index) => {
    let editCopyClone = _.cloneDeep(medications);

    editCopyClone.splice(index, 1);
    noteEditHandler("medications", editCopyClone);
  };

  const handleMedicationChange = (e, index, key) => {
    let editCopyClone = _.cloneDeep(medications);

    editCopyClone[index][key] = e.target.value;
    noteEditHandler("medications", editCopyClone);
  };

  return (
    <>
      {diagnosisProgress.map((diagnosis, index) => {
        return (
          <CardSecondary className="p-3 m-3 row" key={"diagnosis-" + index}>
            <span className="col-12 tw:font-bold">{diagnosis.label}</span>
            <div className="col-12 d-flex justify-content-start">
              {Object.values(RATING_OPTION_LABEL_MAP).map((optionLabel) => (
                <div className="w-25 fs-14 text-center tw:font-bold" key={optionLabel + index}>
                  <div className="m-auto" style={{ width: 50 }}>
                    {optionLabel}
                  </div>
                </div>
              ))}
            </div>

            <div className="col-12 d-flex justify-content-start">
              <BehavioralGoalsRating
                isEditing={true}
                onClick={(e) => handleDiagnosisProgressChange(e, index, "answer", true)}
                optionLabelMap={RATING_OPTION_LABEL_MAP}
                progress={diagnosis}
              />
            </div>

            <span className="tw:font-bold">Assessment</span>
            <textarea
              className="w-100"
              onChange={(e) => handleDiagnosisProgressChange(e, index, "assessment")}
              value={diagnosis.assessment}
            />

            <span className="tw:font-bold">Plan</span>
            <textarea
              className="w-100"
              onChange={(e) => handleDiagnosisProgressChange(e, index, "plan")}
              value={diagnosis.plan}
            />
          </CardSecondary>
        );
      })}

      <div className="d-flex mt-3 tw:justify-center">
        <button className="Btn Btn-sm Btn--pri" onClick={addMedication}>
          Add Medication
        </button>
      </div>

      {medications.map((medication, index) => {
        let isNew = medication.med_changes === "ADD";

        let medChangeOptions;
        let medChangeDefault;

        if (isNew) {
          medChangeOptions = [{ label: "Add", value: "ADD" }];
          medChangeDefault = medChangeOptions[0];
        } else {
          medChangeOptions = MED_CHANGES_OPTIONS;
          medChangeDefault = medChangeOptions.find(
            (option) => option.value === medication.med_changes,
          );
        }

        return (
          <CardSecondary className="p-3 m-3" key={medication.medication_name || index}>
            <p className="my-2 tw:font-bold">Medication</p>
            <input
              className="w-100"
              disabled={!isNew ? true : null}
              onChange={(e) => handleMedicationChange(e, index, "medication_name")}
              value={medication.medication_name}
            />
            <p className="m-2 tw:font-bold">Dosage</p>
            <input
              className="w-100"
              onChange={(e) => handleMedicationChange(e, index, "dosage")}
              value={medication.dosage}
            />

            <div className="d-flex my-2">
              <div className="w-50 tw:font-bold">Medication Changes</div>
              <div className="w-50">
                <Select
                  className="basic-multi-select w-100"
                  classNamePrefix="select"
                  defaultValue={medChangeDefault}
                  isDisabled={isNew ? true : null}
                  onChange={(selectedOption) => {
                    handleMedicationChange(
                      { target: { value: selectedOption.value } },
                      index,
                      "med_changes",
                    );
                  }}
                  options={medChangeOptions}
                />
              </div>
            </div>

            <div className="d-flex my-2">
              <div className="w-25 tw:font-bold">Diagnosis</div>
              <div className="w-75">
                <Select
                  className="basic-multi-select w-100"
                  classNamePrefix="select"
                  defaultValue={medication.diagnosis}
                  isMulti
                  onChange={(selectedOptions) => {
                    handleMedicationChange(
                      { target: { value: selectedOptions } },
                      index,
                      "diagnosis",
                    );
                  }}
                  options={allDiagnosis}
                />
              </div>
            </div>

            {isNew && (
              <div className="d-flex tw:justify-center">
                <button className="Btn Btn-sm Btn--err" onClick={() => deleteMedication(index)}>
                  Delete New Medication
                </button>
              </div>
            )}
          </CardSecondary>
        );
      })}

      <ExpandingSelect
        className="m-3 grey-container"
        disabled
        label="What makes it hard to take your medication?"
        labelClass="tw:font-bold clr-sec"
        options={progressNote.content.medication_difficulties}
      />

      <GenericMultiselect
        className="m-3"
        editable={true}
        editHandler={noteEditHandler}
        isCheckboxes={true}
        labelClass="clr-sec"
        note={progressNote}
        profileView={false}
        sectionKey="adherence_interventions"
        sectionLabel="Adherence Interventions"
      />
    </>
  );
};
